import { create } from 'zustand'

interface UIState {
  sidebarOpen: boolean
  /** Folded sidebar sections by group id. */
  collapsedGroups: Record<string, true>
  darkMode: boolean
  toggleSidebar: () => void
  toggleGroup: (id: string) => void
  setDarkMode: (dark: boolean) => void
}

function applyDarkClass(dark: boolean) {
  document.documentElement.classList.toggle('dark', dark)
}

/** Same rule as the inline script in index.html, which only exists to avoid a light flash before this runs. */
function initialDark(): boolean {
  const saved = localStorage.getItem('darkMode')
  return saved === 'true' || (saved === null && window.matchMedia('(prefers-color-scheme: dark)').matches)
}
const startDark = initialDark()
applyDarkClass(startDark)

function readGroups(): Record<string, true> {
  try {
    const v: unknown = JSON.parse(localStorage.getItem('sidebarGroups') || '{}')
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, true>) : {}
  } catch {
    return {}
  }
}

export const useUIStore = create<UIState>((set) => ({
  sidebarOpen: localStorage.getItem('sidebarOpen') !== 'false',
  collapsedGroups: readGroups(),
  darkMode: startDark,

  toggleSidebar: () =>
    set((state) => {
      const sidebarOpen = !state.sidebarOpen
      localStorage.setItem('sidebarOpen', String(sidebarOpen))
      return { sidebarOpen }
    }),
  toggleGroup: (id) =>
    set((state) => {
      const collapsedGroups = { ...state.collapsedGroups }
      if (collapsedGroups[id]) delete collapsedGroups[id]
      else collapsedGroups[id] = true
      localStorage.setItem('sidebarGroups', JSON.stringify(collapsedGroups))
      return { collapsedGroups }
    }),
  setDarkMode: (dark) => {
    localStorage.setItem('darkMode', String(dark))
    applyDarkClass(dark)
    set({ darkMode: dark })
  },
}))
